import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import deepMerge from '_lib/deepMerge'
import LinearGradients from '../helpers/renderLinearGradients'
import { CHART_COLORS } from '../helpers/colors'
import { PROPTYPES } from '../helpers/propTypes'

import './styles.scss'
import { useLazyLoad } from '_hooks'

// eslint-disable-next-line react/display-name
const renderLabel = (props, ...rest) => {
  const {
    viewBox: { cx, cy, startAngle, endAngle, innerRadius, outerRadius },
    fill,
    value,
    index,
  } = props
  if (value === 0) return null
  const START_DISTANCE = 0
  const ANGLE_DISTANCE = 35
  const END_DISTANCE = ANGLE_DISTANCE + 15
  const RADIAN = Math.PI / 180
  const MOD = index % 2 === 1

  const halfAngle = (startAngle - endAngle) / 2
  const midAngle = startAngle - halfAngle

  // sin e cos can go for 1 to -1. if close to 0 it breaks connections
  const _sin = Math.sin(-RADIAN * midAngle)
  const _cos = Math.cos(-RADIAN * midAngle)
  // hack to prevent values too close to 0
  const sin = _sin < 0.0000001 && _sin > -0.0000001 ? _sin * 1000000000000 : _sin
  const cos = _cos < 0.0000001 && _cos > -0.0000001 ? _cos * 1000000000000 : _cos

  // 1
  const sx = cx + (outerRadius + START_DISTANCE) * cos
  const sy = cy + (outerRadius + START_DISTANCE) * sin
  // 2
  const mx = cx + (outerRadius + ANGLE_DISTANCE + MOD * 20) * cos
  const my = cy + (outerRadius + ANGLE_DISTANCE + MOD * 20) * sin
  // 3
  const ex = cx + (outerRadius + END_DISTANCE + MOD * 20) * cos
  const ey = cy + (outerRadius + END_DISTANCE + MOD * 20) * sin

  return (
    <g>
      <path d={`M${sx},${sy}L${mx},${my}`} stroke={fill} fill="none" />
      <text x={ex} y={ey} textAnchor={'middle'} alignment-baseline={'middle'} fill={fill}>
        <tspan fontSize={22}>{`${value}`}</tspan>
        <tspan fontSize={14}>%</tspan>
      </text>
    </g>
  )
}

const formatLegend = (_, { payload: { value, key } }) => {
  const isEmpty = value === 0
  return (
    <span
      style={{
        color: isEmpty ? '#aaaaaa' : '#3D3D3D',
        textDecoration: isEmpty ? 'line-through' : 'none',
      }}
    >
      {key}
    </span>
  )
}

const PieChart = (props) => {
  const { data, hasLegend, colors, config: configFromProps } = props
  const roundedValues = data.map(({ value }) => value)
  const formattedData = data.map((d, i) => ({ ...d, value: roundedValues[i] }))
  const isAllEmpty = formattedData.reduce((acc, { value }) => acc + value, 0) === 0
  const config = deepMerge(CONFIG_DEFAULT, configFromProps)

  const [
    { LabelList, PieChart: PieChartRecharts, Pie, ResponsiveContainer, Cell, Legend },
    loadingRecharts,
  ] = useLazyLoad(import('recharts'))

  return (
    <If condition={!loadingRecharts}>
      <ResponsiveContainer
        width={config.container.width}
        height={config.container.height}
        className={classNames(
          'pie-chart-recharts',
          { 'pie-chart-recharts--no-data': isAllEmpty },
          config.container.className
        )}
        {...config.container.extraProps}
      >
        <PieChartRecharts {...config.pieChart.extraProps}>
          {LinearGradients(colors, formattedData.length, config.pie.gradientsId)}
          <Pie
            data={formattedData}
            nameKey={config.data.nameKey}
            dataKey={config.data.dataKey}
            cx={config.pie.cx}
            cy={config.pie.cy}
            innerRadius={config.pie.innerRadius}
            outerRadius={config.pie.outerRadius}
            startAngle={90}
            endAngle={-270}
            isAnimationActive={false}
            style={{
              strokeWidth: 0,
              ...config.pie.style,
            }}
            className={config.pie.className}
            {...config.pie.extraProps}
          >
            {formattedData.map((_entry, index) => (
              <Cell key={`cell-${index}`} fill={`url(#${config.pie.gradientsId}${index})`} />
            ))}
            <LabelList
              data={formattedData}
              valueAccessor={config.data.dataKey}
              dataKey={config.data.dataKey}
              content={renderLabel}
              {...config.labelList.extraProps}
            />
          </Pie>
          {hasLegend && (
            <Legend
              layout={config.legend.layout}
              align={config.legend.align}
              verticalAlign={config.legend.verticalAlign}
              iconType={config.legend.iconType}
              iconSize={config.legend.iconSize}
              wrapperStyle={{
                border: '2px solid #EBEBEB',
                padding: 8,
                borderRadius: 16,
                ...config.legend.wrapperStyle,
              }}
              {...config.legend.extraProps}
              formatter={formatLegend}
            />
          )}
        </PieChartRecharts>
      </ResponsiveContainer>
    </If>
  )
}

const CONFIG_DEFAULT = {
  data: {
    nameKey: 'key',
    dataKey: 'value',
  },
  container: {
    width: '100%',
    height: '100%',
    className: '',
    extraProps: {},
  },
  pieChart: {
    extraProps: {},
  },
  pie: {
    innerRadius: '30%',
    outerRadius: '70%',
    className: '',
    style: {},
    cx: '50%',
    cy: '50%',
    gradientsId: 'gradient-',
    extraProps: {},
  },
  labelList: {
    extraProps: {},
  },
  legend: {
    layout: 'vertical',
    align: 'right',
    verticalAlign: 'middle',
    iconType: 'circle',
    iconSize: 40,
    wrapperStyle: {},
    extraProps: {},
  },
}

PieChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.number,
    })
  ).isRequired,
  colors: PROPTYPES.COLORS_GRADIENTS,
  hasLegend: PropTypes.bool,
  config: PropTypes.shape({
    data: PropTypes.shape({
      nameKey: PropTypes.string,
      dataKey: PropTypes.string,
    }),
    container: PropTypes.shape({
      width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      className: PropTypes.string,
      extraProps: PropTypes.shape({}),
    }),
    pieChart: PropTypes.shape({
      extraProps: PropTypes.shape({}),
    }),
    pie: PropTypes.shape({
      innerRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      outerRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      className: PropTypes.string,
      style: PropTypes.shape({}),
      cx: PropTypes.string,
      cy: PropTypes.string,
      gradientsId: PropTypes.string,
      extraProps: PropTypes.shape({}),
    }),
    labelList: PropTypes.shape({
      extraProps: PropTypes.shape({}),
    }),
    legend: PROPTYPES.LEGEND,
  }),
}

PieChart.defaultProps = {
  colors: CHART_COLORS,
  hasLegend: false,
  config: CONFIG_DEFAULT,
}

export default PieChart
